import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import api from "../api";

const MissingKintaiDates = ({
  encodedEmail,
  missingKintaiDates,
  setMissingKintaiDates,
  setSelectedDate,
  onExpandChange,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    date: null,
  });
  const [isExpanded, setIsExpanded] = useState(true);

  // 画面幅に応じて自動的に折りたたみ/展開する
  useEffect(() => {
    const newExpanded = isLargeScreen;
    setIsExpanded(newExpanded);
    if (onExpandChange) {
      onExpandChange(newExpanded);
    }
  }, [isLargeScreen, onExpandChange]);

  const handleToggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    if (onExpandChange) {
      onExpandChange(newExpanded);
    }
  };

  const handleRemoveClick = (date) => {
    setConfirmDialog({
      open: true,
      date: date,
    });
  };

  const handleConfirmClose = () => {
    setConfirmDialog({
      open: false,
      date: null,
    });
  };

  const setDayOff = async () => {
    const { date } = confirmDialog;
    const payload = {
      user_id: encodedEmail,
      date: date.format("YYYY-MM-DD"),
    };
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/dayoff/`, payload);
      setMissingKintaiDates((prevDates) => prevDates.filter((status) => status.date !== date.format("YYYY-MM-DD")));
      handleConfirmClose();
    } catch (error) {
      console.error("Error registering day-off:", error);
      handleConfirmClose();
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mt: 4,
        width: isExpanded ? 250 : 10,
        maxWidth: isExpanded ? "25vw" : 10,
        minWidth: isExpanded ? 180 : 10,
        transition: "width 0.3s ease-in-out",
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent={isExpanded ? "space-between" : "center"}
        sx={{ cursor: "pointer", minHeight: "40px" }}
        onClick={handleToggleExpanded}
      >
        {isExpanded && (
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            勤怠未登録一覧
          </Typography>
        )}
        <IconButton
          sx={{
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      {isExpanded && (
        <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
          {missingKintaiDates !== null
            ? (
              missingKintaiDates
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((status, index) => {
                  // 勤怠登録済み（サーバー反映済み）または休暇の場合は非表示
                  // ただし、保留中（is_pending）の場合は表示する
                  if ((status.is_registered && !status.is_pending) || status.is_dayoff) {
                    return null;
                  }
                  return (
                    <Box
                      display="flex"
                      flexDirection="column"
                      key={index}
                      sx={{
                        mb: 1,
                        p: 1,
                        border: status.is_pending ? "1px solid #ff9800" : "1px solid #e0e0e0",
                        borderRadius: 1,
                        backgroundColor: status.is_pending ? "#fff3e0" : "transparent",
                        ":hover": {
                          backgroundColor: status.is_pending ? "#ffe0b2" : "#f5f5f5",
                          cursor: "pointer",
                        },
                      }}
                      onClick={() => setSelectedDate(dayjs(status.date))}
                    >
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {status.date}
                        </Typography>
                        {status.is_pending ? (
                          <Chip
                            label="反映中"
                            size="small"
                            color="warning"
                            sx={{ fontSize: "0.7rem", height: "20px" }}
                          />
                        ) : (
                          <Button
                            sx={{
                              color: "white",
                              backgroundColor: "#1976D2",
                              "&:hover": {
                                backgroundColor: "#115293",
                                color: "white",
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // クリックイベントの伝播を防ぐ
                              handleRemoveClick(dayjs(status.date));
                            }}
                          >
                            休暇登録
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })
            )
            : (
              <Typography variant="body2" color="text.secondary">
                データを読み込み中...
              </Typography>
            )}
        </Box>
      )}
      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          休暇情報の登録確認
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ whiteSpace: "pre-line" }}
          >
            {confirmDialog.date
              && `${
                confirmDialog.date.format(
                  "YYYY年MM月DD日",
                )
              }の勤怠を休暇として登録しますか？\r\n※freeeの休暇申請はfreeeから登録して下さい。`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} color="primary">
            キャンセル
          </Button>
          <Button onClick={setDayOff} color="primary" autoFocus>
            登録する
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MissingKintaiDates;
